import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2, Users, Shield, Key, Plus, Search, Filter,
  ChevronDown, Settings, MoreVertical, Eye, Edit, Trash2,
  CheckCircle, XCircle, Clock, AlertTriangle, Crown,
  TrendingUp, DollarSign, UserCheck, Activity, Package,
  Layers, Save, RefreshCw, FileText, ThumbsUp, ThumbsDown, Zap, Database, Pause
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  listTenants, createTenant, deleteTenant, getLicenseTiers,
  getMyTenants, getPlatformStats, listAllUsers, getModules,
  updateTenantModules, getTenantVisibleFrameworks,
  updateTenantVisibleFrameworks, getAvailableFrameworks,
  listFrameworkRequests, updateFrameworkRequest, addPreviewModules,
  LICENSE_TIER_COLORS, STATUS_COLORS, MODULE_ICONS
} from '@/services/admin';
import api from '@/services/api';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [licenseTiers, setLicenseTiers] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newTenant, setNewTenant] = useState({ name: '', slug: '', owner_email: '', license_tier: 'free', domains: '', max_users: 5 });
  const [platformStats, setPlatformStats] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [activeTab, setActiveTab] = useState('tenants');

  // Maintenance: Recount scan summaries (Amidala CSPM)
  const [maintDays, setMaintDays] = useState(30);
  const [maintTenantId, setMaintTenantId] = useState('');
  const [maintPreview, setMaintPreview] = useState(null);
  const [maintBusy, setMaintBusy] = useState(false);
  const runBackfill = async (dry_run) => {
    try {
      setMaintBusy(true);
      const params = { dry_run, days: maintDays };
      if (maintTenantId?.trim()) params.tenant_id = maintTenantId.trim();
      const res = await api.post('/admin/maintenance/backfill-scan-summaries', null, { params });
      setMaintPreview(res.data);
      if (dry_run) {
        toast.success(`Preview: ${res.data.scans_needing_update} of ${res.data.scans_checked} scan(s) need updating.`);
      } else {
        toast.success(`Backfill applied — ${res.data.scans_updated} scan(s) recounted.`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Backfill failed');
    } finally {
      setMaintBusy(false);
    }
  };

  // Module Management
  const [showModuleDialog, setShowModuleDialog] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [availableModules, setAvailableModules] = useState([]);
  const [selectedModules, setSelectedModules] = useState([]);
  const [savingModules, setSavingModules] = useState(false);

  // Visible Frameworks Management
  const [showFrameworkDialog, setShowFrameworkDialog] = useState(false);
  const [frameworkTenant, setFrameworkTenant] = useState(null);
  const [allFrameworks, setAllFrameworks] = useState([]);
  const [visibleFrameworks, setVisibleFrameworks] = useState([]);
  const [frameworkSearch, setFrameworkSearch] = useState('');
  const [savingFrameworks, setSavingFrameworks] = useState(false);

  // Framework Requests
  const [frameworkRequests, setFrameworkRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(false);

  // Preview Modules
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [previewUser, setPreviewUser] = useState(null);
  const [previewModules, setPreviewModules] = useState([]);
  const [savingPreview, setSavingPreview] = useState(false);

  // Dashboard Widgets
  const [widgetData, setWidgetData] = useState(null);
  const [widgetLoading, setWidgetLoading] = useState(false);

  // Audit Logs
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditSearch, setAuditSearch] = useState('');

  // Preview Requests
  const [previewRequests, setPreviewRequests] = useState([]);
  const [previewReqLoading, setPreviewReqLoading] = useState(false);

  // Feedback
  const [feedbackItems, setFeedbackItems] = useState([]);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [feedbackFilter, setFeedbackFilter] = useState('all');
  const [feedbackPreview, setFeedbackPreview] = useState(null);

  const TIER_COLORS = { free: 'bg-slate-100 text-slate-600', starter: 'bg-blue-100 text-blue-700', professional: 'bg-purple-100 text-purple-700', enterprise: 'bg-amber-100 text-amber-700' };

  useEffect(() => { loadData(); }, [statusFilter]);

  const loadData = async () => {
    try {
      setLoading(true);
      const myTenants = await getMyTenants();
      setIsSuperAdmin(myTenants.is_super_admin);
      if (myTenants.is_super_admin) {
        const [tenantsData, tiersData, statsData] = await Promise.all([
          listTenants({ status: statusFilter || undefined, limit: 50 }),
          getLicenseTiers(),
          getPlatformStats().catch(() => null)
        ]);
        setTenants(tenantsData.items || []);
        setLicenseTiers(tiersData.tiers || []);
        if (statsData) setPlatformStats(statsData);
      } else {
        setTenants(myTenants.tenants || []);
      }
    } catch (err) {
      console.error('Failed to load data:', err);
      toast.error(err.response?.status === 403 ? 'You do not have admin access' : 'Failed to load admin data');
    } finally { setLoading(false); }
  };

  const handleCreateTenant = async () => {
    if (!newTenant.name || !newTenant.slug || !newTenant.owner_email) { toast.error('Please fill in all required fields'); return; }
    setCreating(true);
    try {
      await createTenant({ ...newTenant, domains: newTenant.domains ? newTenant.domains.split(',').map(d => d.trim()) : [], enabled_modules: ['grc'] });
      toast.success('Tenant created successfully');
      setShowCreateDialog(false);
      setNewTenant({ name: '', slug: '', owner_email: '', license_tier: 'free', domains: '', max_users: 5 });
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create tenant'); }
    finally { setCreating(false); }
  };

  const loadUsers = async (search = '') => {
    setUsersLoading(true);
    try { const data = await listAllUsers({ search: search || undefined, per_page: 50 }); setAllUsers(data.users || []); }
    catch { toast.error('Failed to load users'); }
    finally { setUsersLoading(false); }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'users' && allUsers.length === 0) loadUsers();
    if (tab === 'requests') loadFrameworkRequests();
    if (tab === 'insights' && !widgetData) loadWidgets();
    if (tab === 'audit') loadAuditLogs();
    if (tab === 'preview-requests') loadPreviewRequests();
    if (tab === 'feedback') loadFeedback();
  };

  const loadFeedback = async () => {
    setFeedbackLoading(true);
    try {
      const params = feedbackFilter !== 'all' ? `?rating=${feedbackFilter}` : '';
      const res = await api.get(`/feedback${params}`);
      setFeedbackItems(res.data.items || []);
    } catch { toast.error('Failed to load feedback'); }
    finally { setFeedbackLoading(false); }
  };

  const loadWidgets = async () => {
    setWidgetLoading(true);
    try { const data = await api.get('/admin/dashboard-widgets'); setWidgetData(data.data); }
    catch { toast.error('Failed to load dashboard data'); }
    finally { setWidgetLoading(false); }
  };

  const loadAuditLogs = async (search = '') => {
    setAuditLoading(true);
    try {
      const params = { limit: 50 };
      if (search) params.action = search;
      const data = await api.get('/admin/audit-logs', { params });
      setAuditLogs(data.data.items || []);
    } catch { toast.error('Failed to load audit logs'); }
    finally { setAuditLoading(false); }
  };

  const loadPreviewRequests = async () => {
    setPreviewReqLoading(true);
    try { const data = await api.get('/admin/preview-requests'); setPreviewRequests(data.data.items || []); }
    catch { toast.error('Failed to load preview requests'); }
    finally { setPreviewReqLoading(false); }
  };

  const handlePreviewRequestAction = async (requestId, action) => {
    try {
      await api.put(`/api/admin/preview-requests/${requestId}`, null, { params: { action } });
      toast.success(`Preview request ${action}d`);
      loadPreviewRequests();
      loadWidgets();
    } catch (err) { toast.error(err.response?.data?.detail || `Failed to ${action} request`); }
  };

  const handleDisableTenant = async (tenantId, tenantName) => {
    const reason = window.prompt(`Disable tenant "${tenantName}"?\n\nUsers will lose access until re-enabled. Existing data is preserved.\n\nReason:`);
    if (!reason) return;
    try {
      await api.post(`/admin/tenants/${tenantId}/disable?reason=${encodeURIComponent(reason)}`);
      toast.success(`Tenant "${tenantName}" disabled`);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to disable tenant'); }
  };

  const handleEnableTenant = async (tenantId, tenantName) => {
    try {
      await api.post(`/admin/tenants/${tenantId}/enable`);
      toast.success(`Tenant "${tenantName}" re-enabled`);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to enable tenant'); }
  };

  const handleDeleteTenant = async (tenantId, tenantName, isDisabled) => {
    // Two-stage lifecycle (Feb 2026): tenant MUST be disabled before delete.
    if (!isDisabled) {
      if (!window.confirm(`"${tenantName}" is still active.\n\nBeskar requires tenants to be disabled BEFORE permanent deletion (safety guard). Disable it now?`)) return;
      await handleDisableTenant(tenantId, tenantName);
      return; // let the operator review the disabled state before pressing Delete again
    }
    const reason = window.prompt(
      `PERMANENTLY delete tenant "${tenantName}"?\n\n` +
      `This will CASCADE WIPE every tenant-scoped collection across:\n` +
      `  • Amidala (accounts, scans, findings, coverage, snapshots, evidence, exceptions, alerts, operator creds)\n` +
      `  • LiteSabre + LiteSabre Pro (scans, findings, SCM creds)\n` +
      `  • GRC (controls, evidence, policies, vendors, audit engagements, risk register)\n` +
      `  • Users (memberships, roles), SCIM configs, SAML config, webhooks, audit trails\n\n` +
      `THIS CANNOT BE UNDONE. Type the tenant name to confirm:`
    );
    if (!reason) return;
    if (reason.trim() !== tenantName.trim()) {
      toast.error('Tenant name did not match — delete cancelled.');
      return;
    }
    try {
      const r = await api.delete(`/admin/tenants/${tenantId}`);
      toast.success(`Deleted "${tenantName}" — ${r.data?.rows_deleted || 0} rows across ${r.data?.collections_touched || 0} collections`);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete tenant'); }
  };

  const handleDeleteUser = async (user) => {
    const reason = window.prompt(`Delete user "${user.email}" and ALL associated data?\n\nThis removes: account, tenant memberships, training progress, feedback, sessions.\n\nThis action is IRREVERSIBLE.\n\nEnter business justification to confirm:`);
    if (!reason) return;
    try {
      await api.delete(`/admin/users/${user.id}`);
      toast.success(`User "${user.email}" deleted`);
      loadUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete user'); }
  };

  // Module Management
  const openModuleDialog = async (tenant) => {
    setSelectedTenant(tenant);
    setSelectedModules(tenant.enabled_modules || []);
    if (availableModules.length === 0) {
      try { const data = await getModules(); setAvailableModules(data.modules || []); }
      catch { setAvailableModules([
        { id: 'grc', name: 'GRC Nexus', description: 'Governance, Risk & Compliance' },
        { id: 'osint', name: 'OSINT', description: 'Open Source Intelligence' },
        { id: 'litesabre', name: 'LiteSabre', description: 'Vulnerability Scanner' },
        { id: 'amidala_cspm', name: 'Amidala CSPM', description: 'Cloud Security Posture Management' },
        { id: 'active_directory', name: 'Active Directory', description: 'AD Security Analysis' }
      ]); }
    }
    setShowModuleDialog(true);
  };

  const handleModuleToggle = (moduleId) => setSelectedModules(prev => prev.includes(moduleId) ? prev.filter(m => m !== moduleId) : [...prev, moduleId]);

  const handleSaveModules = async () => {
    if (!selectedTenant) return;
    setSavingModules(true);
    try { await updateTenantModules(selectedTenant.id, selectedModules); toast.success(`Modules updated for ${selectedTenant.name}`); setShowModuleDialog(false); loadData(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed to update modules'); }
    finally { setSavingModules(false); }
  };

  const getModuleIcon = (moduleId) => ({ grc: '📊', osint: '🔍', litesabre: '⚔️', amidala_cspm: '☁️', active_directory: '🏢', sbom: '📦' }[moduleId] || '📦');

  // Visible Frameworks Management
  const openFrameworkDialog = async (tenant) => {
    setFrameworkTenant(tenant);
    setFrameworkSearch('');
    try {
      const [fwData, visData] = await Promise.all([
        getAvailableFrameworks(),
        getTenantVisibleFrameworks(tenant.id)
      ]);
      setAllFrameworks(fwData.frameworks || []);
      setVisibleFrameworks(visData.visible_frameworks || []);
    } catch (err) {
      toast.error('Failed to load frameworks');
      return;
    }
    setShowFrameworkDialog(true);
  };

  const handleFrameworkToggle = (code) => {
    const upper = code.toUpperCase();
    setVisibleFrameworks(prev => prev.includes(upper) ? prev.filter(f => f !== upper) : [...prev, upper]);
  };

  const handleSaveFrameworks = async () => {
    if (!frameworkTenant) return;
    setSavingFrameworks(true);
    try {
      await updateTenantVisibleFrameworks(frameworkTenant.id, visibleFrameworks);
      toast.success(`Visible frameworks updated for ${frameworkTenant.name}`);
      setShowFrameworkDialog(false);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update frameworks'); }
    finally { setSavingFrameworks(false); }
  };

  // Framework Requests
  const loadFrameworkRequests = async () => {
    setRequestsLoading(true);
    try { const data = await listFrameworkRequests(); setFrameworkRequests(data.items || []); }
    catch { toast.error('Failed to load framework requests'); }
    finally { setRequestsLoading(false); }
  };

  const handleRequestAction = async (requestId, action) => {
    try {
      await updateFrameworkRequest(requestId, action);
      toast.success(`Request ${action}d`);
      loadFrameworkRequests();
    } catch (err) { toast.error(err.response?.data?.detail || `Failed to ${action} request`); }
  };

  // Preview Modules
  const openPreviewDialog = (user) => {
    setPreviewUser(user);
    setPreviewModules(['grc']);
    setShowPreviewDialog(true);
  };

  const handlePreviewToggle = (moduleId) => setPreviewModules(prev => prev.includes(moduleId) ? prev.filter(m => m !== moduleId) : [...prev, moduleId]);

  const handleSavePreview = async () => {
    if (!previewUser || previewModules.length === 0) { toast.error('Select at least one module'); return; }
    setSavingPreview(true);
    try {
      const result = await addPreviewModules(previewUser.id, previewModules);
      toast.success(`Preview activated for ${previewUser.email} (7 days) — slug: ${result.slug}`);
      setShowPreviewDialog(false);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to activate preview'); }
    finally { setSavingPreview(false); }
  };

  const filteredTenants = tenants.filter(t =>
    t.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.slug?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'suspended': return <AlertTriangle className="w-4 h-4 text-yellow-500" />;
      case 'pending': return <Clock className="w-4 h-4 text-blue-500" />;
      case 'deactivated': return <XCircle className="w-4 h-4 text-red-500" />;
      default: return null;
    }
  };

  const filteredAllFrameworks = allFrameworks.filter(fw =>
    fw.name?.toLowerCase().includes(frameworkSearch.toLowerCase()) ||
    fw.code?.toLowerCase().includes(frameworkSearch.toLowerCase())
  );

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="grid grid-cols-4 gap-4">{[1, 2, 3, 4].map(i => <div key={i} className="h-28 bg-gray-200 rounded"></div>)}</div>
          <div className="h-96 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            {isSuperAdmin && <Crown className="w-7 h-7 text-amber-500" />}
            <Shield className="w-7 h-7 text-indigo-600" />
            Admin Portal
          </h1>
          <p className="text-gray-500 mt-1">{isSuperAdmin ? 'Manage tenants, licenses, and platform administration' : 'Manage your organization'}</p>
        </div>
        {isSuperAdmin && (
          <Button onClick={() => setShowCreateDialog(true)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="create-tenant-btn">
            <Plus className="w-4 h-4 mr-2" />Create Tenant
          </Button>
        )}
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
        {[
          { label: 'Total Tenants', value: tenants.length, color: 'border-l-indigo-500', icon: <Building2 className="w-5 h-5 text-indigo-600" />, bg: 'bg-indigo-100' },
          { label: 'Active Tenants', value: tenants.filter(t => t.status === 'active').length, color: 'border-l-emerald-500', icon: <CheckCircle className="w-5 h-5 text-emerald-600" />, bg: 'bg-emerald-100' },
          { label: 'Platform Users', value: platformStats?.total_users ?? tenants.reduce((s, t) => s + (t.member_count || 0), 0), color: 'border-l-blue-500', icon: <Users className="w-5 h-5 text-blue-600" />, bg: 'bg-blue-100' },
          { label: 'Active (7d)', value: platformStats?.active_7d ?? '—', color: 'border-l-violet-500', icon: <Activity className="w-5 h-5 text-violet-600" />, bg: 'bg-violet-100' },
          { label: 'Est. MRR', value: platformStats ? `$${platformStats.total_mrr.toLocaleString()}` : '—', color: 'border-l-amber-500', icon: <DollarSign className="w-5 h-5 text-amber-600" />, bg: 'bg-amber-100' },
        ].map((m, i) => (
          <Card key={i} className={`border-l-4 ${m.color}`}>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center justify-between">
                <div><p className="text-xs text-muted-foreground">{m.label}</p><p className="text-2xl font-bold mt-0.5" data-testid={`stat-${m.label.toLowerCase().replace(/\s/g, '-')}`}>{m.value}</p></div>
                <div className={`p-2 rounded-full ${m.bg}`}>{m.icon}</div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Tabbed content */}
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList>
          <TabsTrigger value="tenants">Tenants</TabsTrigger>
          {isSuperAdmin && <TabsTrigger value="users" data-testid="users-tab">Users</TabsTrigger>}
          {isSuperAdmin && <TabsTrigger value="requests" data-testid="requests-tab">FW Requests</TabsTrigger>}
          {isSuperAdmin && <TabsTrigger value="preview-requests" data-testid="preview-requests-tab">Preview Requests</TabsTrigger>}
          {isSuperAdmin && <TabsTrigger value="insights" data-testid="insights-tab">Insights</TabsTrigger>}
          {isSuperAdmin && <TabsTrigger value="audit" data-testid="audit-tab">Audit Logs</TabsTrigger>}
          {isSuperAdmin && <TabsTrigger value="feedback" data-testid="feedback-tab">Feedback</TabsTrigger>}
        </TabsList>

        {/* ─── Tenants Tab ─────────────────────────────────────────────── */}
        <TabsContent value="tenants" className="space-y-4 mt-4">
          <Card>
            <CardContent className="py-4">
              <div className="flex flex-wrap gap-4">
                <div className="flex-1 min-w-[200px]">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input placeholder="Search tenants..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" data-testid="tenant-search" />
                  </div>
                </div>
                <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
                  <SelectTrigger className="w-[150px]"><SelectValue placeholder="All Status" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="deactivated">Deactivated</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-lg">Tenants</CardTitle><CardDescription>{filteredTenants.length} tenants found</CardDescription></CardHeader>
            <CardContent>
              {filteredTenants.length === 0 ? (
                <div className="text-center py-12">
                  <Building2 className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500 mb-4">No tenants found</p>
                  {isSuperAdmin && <Button onClick={() => setShowCreateDialog(true)}>Create Your First Tenant</Button>}
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredTenants.map((tenant) => (
                    <div key={tenant.id} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors" data-testid={`tenant-${tenant.id}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-semibold text-lg">{tenant.name}</h3>
                            {tenant.is_disabled && (
                              <span className="text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-700 border border-amber-300" data-testid={`tenant-disabled-badge-${tenant.id}`}>
                                <Pause className="w-3 h-3 inline mr-1" />
                                DISABLED
                              </span>
                            )}
                            <span className={`text-xs px-2 py-1 rounded-full ${STATUS_COLORS[tenant.status] || 'bg-gray-100'}`}>
                              {getStatusIcon(tenant.status)}<span className="ml-1">{tenant.status || 'unknown'}</span>
                            </span>
                            <span className={`text-xs px-2 py-1 rounded-full border ${LICENSE_TIER_COLORS[tenant.license_tier] || 'bg-gray-100'}`}>{tenant.license_tier || 'free'}</span>
                            {tenant.is_preview && <Badge variant="outline" className="text-xs border-orange-300 text-orange-600">Preview</Badge>}
                          </div>
                          <p className="text-sm text-gray-500 mb-3">
                            <code className="bg-gray-100 px-1 rounded">{tenant.slug}</code>
                            {tenant.domains?.length > 0 && <span className="ml-2">• {tenant.domains.join(', ')}</span>}
                          </p>
                          <div className="flex flex-wrap gap-4 text-sm">
                            <div className="flex items-center gap-1"><Users className="w-4 h-4 text-gray-400" /><span>{tenant.member_count || 0} / {tenant.max_users || '?'} users</span></div>
                            <div className="flex items-center gap-2">
                              <span className="text-gray-400">Modules:</span>
                              {(tenant.enabled_modules || []).map(m => <span key={m} title={m} className="text-lg">{MODULE_ICONS[m] || '📦'}</span>)}
                            </div>
                            <div className="text-gray-400">Created: {tenant.created_at ? new Date(tenant.created_at).toLocaleDateString() : 'N/A'}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 ml-4">
                          <Button variant="outline" size="sm" onClick={() => navigate(`/admin/tenants/${tenant.id}`)}><Eye className="w-4 h-4 mr-1" />View</Button>
                          {isSuperAdmin && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild><Button variant="ghost" size="sm"><MoreVertical className="w-4 h-4" /></Button></DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => navigate(`/admin/tenants/${tenant.id}`)}><Edit className="w-4 h-4 mr-2" />Edit</DropdownMenuItem>
                                <DropdownMenuItem onClick={() => navigate(`/admin/tenants/${tenant.id}/members`)}><Users className="w-4 h-4 mr-2" />Manage Members</DropdownMenuItem>
                                <DropdownMenuItem onClick={() => openModuleDialog(tenant)} data-testid={`manage-modules-${tenant.id}`}><Layers className="w-4 h-4 mr-2" />Manage Modules</DropdownMenuItem>
                                <DropdownMenuItem onClick={() => openFrameworkDialog(tenant)} data-testid={`manage-frameworks-${tenant.id}`}><FileText className="w-4 h-4 mr-2" />Visible Frameworks</DropdownMenuItem>
                                <DropdownMenuSeparator />
                                {tenant.is_disabled ? (
                                  <DropdownMenuItem className="text-emerald-600" onClick={() => handleEnableTenant(tenant.id, tenant.name)} data-testid={`enable-tenant-${tenant.id}`}>
                                    <RefreshCw className="w-4 h-4 mr-2" />Re-enable Tenant
                                  </DropdownMenuItem>
                                ) : (
                                  <DropdownMenuItem className="text-amber-600" onClick={() => handleDisableTenant(tenant.id, tenant.name)} data-testid={`disable-tenant-${tenant.id}`}>
                                    <Pause className="w-4 h-4 mr-2" />Disable Tenant
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem className="text-red-600" onClick={() => handleDeleteTenant(tenant.id, tenant.name, tenant.is_disabled)} data-testid={`delete-tenant-${tenant.id}`}>
                                  <Trash2 className="w-4 h-4 mr-2" />Delete Tenant {!tenant.is_disabled && '(Disable first)'}
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
          {isSuperAdmin && (
            <Card>
              <CardHeader><CardTitle className="text-lg">Quick Actions</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { label: 'Super Admins', icon: <Crown className="w-5 h-5 text-amber-500" />, to: '/admin/super-admins' },
                    { label: 'Roles & Permissions', icon: <Shield className="w-5 h-5 text-indigo-500" />, to: '/admin/roles' },
                    { label: 'License Tiers', icon: <Package className="w-5 h-5 text-green-500" />, to: '/admin/licenses' },
                    { label: 'Audit Logs', icon: <Activity className="w-5 h-5 text-slate-500" />, to: '/admin/audit-logs' },
                    { label: 'Vulnerability DB', icon: <Database className="w-5 h-5 text-red-500" />, to: '/admin/vulnerability-db' },
                  ].map((a, i) => (
                    <Button key={i} variant="outline" className="h-20 flex-col gap-2" onClick={() => navigate(a.to)}>{a.icon}<span>{a.label}</span></Button>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Maintenance — CSPM scan summary backfill */}
          {isSuperAdmin && (
            <Card data-testid="maintenance-card">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <RefreshCw className="w-5 h-5 text-indigo-500" />
                  Recount Scan Summaries
                </CardTitle>
                <CardDescription>
                  Recomputes <code>total_checks / passed_checks / failed_checks</code> on
                  Amidala scan documents from the underlying findings. Use this after any
                  drift between the &ldquo;Latest Scan&rdquo; tile and the account-level totals.
                  Dry-run first, then apply.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                  <div>
                    <Label htmlFor="maint-days">Window (days)</Label>
                    <Input
                      id="maint-days"
                      type="number"
                      min={1}
                      max={365}
                      value={maintDays}
                      onChange={(e) => setMaintDays(Math.max(1, Math.min(365, parseInt(e.target.value || '30', 10))))}
                      data-testid="maint-days-input"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="maint-tenant-id">Tenant ID (optional)</Label>
                    <Input
                      id="maint-tenant-id"
                      placeholder="Leave blank to backfill every tenant"
                      value={maintTenantId}
                      onChange={(e) => setMaintTenantId(e.target.value)}
                      data-testid="maint-tenant-input"
                    />
                  </div>
                </div>
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    disabled={maintBusy}
                    onClick={() => runBackfill(true)}
                    data-testid="maint-preview-btn"
                  >
                    {maintBusy ? 'Working…' : 'Preview (dry run)'}
                  </Button>
                  <Button
                    className="bg-indigo-600 hover:bg-indigo-700 text-white"
                    disabled={maintBusy || !maintPreview || maintPreview.scans_needing_update === 0}
                    onClick={() => runBackfill(false)}
                    data-testid="maint-apply-btn"
                  >
                    {maintBusy ? 'Working…' : `Apply${maintPreview ? ` (${maintPreview.scans_needing_update})` : ''}`}
                  </Button>
                </div>
                {maintPreview && (
                  <div className="rounded border bg-slate-50 p-3 text-xs space-y-2" data-testid="maint-result">
                    <div className="flex gap-4 flex-wrap">
                      <span><b>Mode:</b> {maintPreview.dry_run ? 'dry run' : 'applied'}</span>
                      <span><b>Window:</b> {maintPreview.window_days}d</span>
                      <span><b>Checked:</b> {maintPreview.scans_checked}</span>
                      <span><b>Needing update:</b> {maintPreview.scans_needing_update}</span>
                      <span><b>Updated:</b> {maintPreview.scans_updated ?? 0}</span>
                    </div>
                    {(maintPreview.sample_diffs || []).length > 0 && (
                      <details>
                        <summary className="cursor-pointer text-indigo-600">
                          Show first {maintPreview.sample_diffs.length} diff(s)
                        </summary>
                        <ul className="mt-2 space-y-1">
                          {maintPreview.sample_diffs.map((d) => (
                            <li key={d.scan_id} className="font-mono">
                              {d.scan_id.slice(0, 8)}…: {d.before.total}/{d.before.passed}/{d.before.failed}
                              {' → '}
                              {d.after.total}/{d.after.passed}/{d.after.failed}
                            </li>
                          ))}
                        </ul>
                      </details>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ─── Users Tab ──────────────────────────────────────── */}
        {isSuperAdmin && (
          <TabsContent value="users" className="space-y-4 mt-4">
            {platformStats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { label: 'Total Users', val: platformStats.total_users, icon: <Users className="w-4 h-4 text-blue-600" />, color: 'bg-blue-50' },
                  { label: 'Active (7 days)', val: platformStats.active_7d, icon: <UserCheck className="w-4 h-4 text-emerald-600" />, color: 'bg-emerald-50' },
                  { label: 'Active (30 days)', val: platformStats.active_30d, icon: <Activity className="w-4 h-4 text-violet-600" />, color: 'bg-violet-50' },
                  { label: 'New (30 days)', val: platformStats.new_users_30d, icon: <TrendingUp className="w-4 h-4 text-amber-600" />, color: 'bg-amber-50' },
                ].map((m, i) => (
                  <Card key={i}><CardContent className="pt-4 pb-4"><div className={`inline-flex p-2 rounded-lg ${m.color} mb-2`}>{m.icon}</div><p className="text-xl font-bold" data-testid={`user-stat-${i}`}>{m.val}</p><p className="text-xs text-muted-foreground">{m.label}</p></CardContent></Card>
                ))}
              </div>
            )}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Registered Users</CardTitle>
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                    <Input placeholder="Search users..." value={userSearch} onChange={(e) => { setUserSearch(e.target.value); loadUsers(e.target.value); }} className="pl-8 h-8 w-48 text-sm" data-testid="user-search-input" />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {usersLoading ? (
                  <div className="text-center py-10 text-muted-foreground text-sm">Loading users...</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-muted/50">
                          <th className="text-left px-4 py-2 font-medium text-muted-foreground">Name</th>
                          <th className="text-left px-4 py-2 font-medium text-muted-foreground">Email</th>
                          <th className="text-left px-4 py-2 font-medium text-muted-foreground">Type</th>
                          <th className="text-left px-4 py-2 font-medium text-muted-foreground">Registered</th>
                          <th className="text-left px-4 py-2 font-medium text-muted-foreground">Last Sign In</th>
                          <th className="text-left px-4 py-2 font-medium text-muted-foreground">Tenants</th>
                          <th className="text-left px-4 py-2 font-medium text-muted-foreground">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {allUsers.map((u) => (
                          <tr key={u.id} className="hover:bg-muted/30 transition" data-testid={`user-row-${u.id}`}>
                            <td className="px-4 py-2.5 font-medium">{u.full_name || '—'}</td>
                            <td className="px-4 py-2.5 text-muted-foreground">{u.email}</td>
                            <td className="px-4 py-2.5">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${u.account_type === 'business' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'}`}>{u.account_type || 'user'}</span>
                            </td>
                            <td className="px-4 py-2.5 text-muted-foreground text-xs">{u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}</td>
                            <td className="px-4 py-2.5 text-muted-foreground text-xs">
                              {u.last_login ? <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block"></span>{new Date(u.last_login).toLocaleDateString()}</span> : <span className="text-slate-400">Never</span>}
                            </td>
                            <td className="px-4 py-2.5">
                              <div className="flex flex-wrap gap-1">
                                {u.tenants?.slice(0, 2).map(t => <span key={t.id} className="px-1.5 py-0.5 bg-indigo-50 text-indigo-600 rounded text-xs">{t.name}</span>)}
                                {u.tenants?.length > 2 && <span className="text-xs text-muted-foreground">+{u.tenants.length - 2}</span>}
                              </div>
                            </td>
                            <td className="px-4 py-2.5">
                              <div className="flex gap-1">
                                <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => openPreviewDialog(u)} data-testid={`preview-btn-${u.id}`}>
                                  <Zap className="w-3 h-3 mr-1" />Preview
                                </Button>
                                <Button variant="outline" size="sm" className="h-7 text-xs text-red-600 hover:bg-red-50 hover:text-red-700" onClick={() => handleDeleteUser(u)} data-testid={`delete-user-btn-${u.id}`}>
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                        {allUsers.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-muted-foreground">No users found</td></tr>}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* ─── Framework Requests Tab ──────────────────────────────── */}
        {isSuperAdmin && (
          <TabsContent value="requests" className="space-y-4 mt-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2"><FileText className="w-5 h-5 text-indigo-600" />Framework Requests</CardTitle>
                  <Button variant="outline" size="sm" onClick={loadFrameworkRequests}><RefreshCw className="w-3.5 h-3.5 mr-1" />Refresh</Button>
                </div>
                <CardDescription>Approve or reject tenant requests to enable compliance frameworks</CardDescription>
              </CardHeader>
              <CardContent>
                {requestsLoading ? (
                  <div className="text-center py-10 text-muted-foreground text-sm">Loading requests...</div>
                ) : frameworkRequests.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <FileText className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                    <p>No framework requests</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {frameworkRequests.map((req) => (
                      <div key={req.id} className="border rounded-lg p-4 flex items-center justify-between" data-testid={`fw-request-${req.id}`}>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-semibold text-sm">{req.framework_code}</span>
                            <Badge variant={req.status === 'pending' ? 'outline' : req.status === 'approved' ? 'default' : 'destructive'} className="text-xs capitalize">{req.status}</Badge>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Tenant: {req.tenant_name || req.tenant_id} • By: {req.requested_by_email} • {new Date(req.created_at).toLocaleDateString()}
                          </p>
                          {req.reason && <p className="text-xs text-muted-foreground mt-1 italic">"{req.reason}"</p>}
                        </div>
                        {req.status === 'pending' && (
                          <div className="flex gap-2">
                            <Button size="sm" className="h-7 bg-emerald-600 hover:bg-emerald-700 text-xs" onClick={() => handleRequestAction(req.id, 'approve')} data-testid={`approve-${req.id}`}>
                              <ThumbsUp className="w-3 h-3 mr-1" />Approve
                            </Button>
                            <Button size="sm" variant="outline" className="h-7 text-xs text-red-600 border-red-200 hover:bg-red-50" onClick={() => handleRequestAction(req.id, 'reject')} data-testid={`reject-${req.id}`}>
                              <ThumbsDown className="w-3 h-3 mr-1" />Reject
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* ─── Preview Requests Tab ──────────────────────────── */}
        {isSuperAdmin && (
          <TabsContent value="preview-requests" className="space-y-4 mt-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2"><Zap className="w-5 h-5 text-orange-500" />Preview Access Requests</CardTitle>
                  <Button variant="outline" size="sm" onClick={loadPreviewRequests}><RefreshCw className="w-3.5 h-3.5 mr-1" />Refresh</Button>
                </div>
                <CardDescription>Users requesting preview access to modules</CardDescription>
              </CardHeader>
              <CardContent>
                {previewReqLoading ? (
                  <div className="text-center py-10 text-muted-foreground text-sm">Loading...</div>
                ) : previewRequests.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Zap className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                    <p>No preview requests</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {previewRequests.map((req) => (
                      <div key={req.id} className="border rounded-lg p-4 flex items-center justify-between" data-testid={`preview-req-${req.id}`}>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-semibold text-sm">{req.user_email}</span>
                            <Badge variant={req.status === 'pending' ? 'outline' : req.status === 'approved' ? 'default' : 'destructive'} className="text-xs capitalize">{req.status}</Badge>
                          </div>
                          <p className="text-xs text-muted-foreground">Modules: {(req.modules || []).join(', ')} &bull; {new Date(req.created_at).toLocaleDateString()}</p>
                          {req.reason && <p className="text-xs text-muted-foreground mt-1 italic">"{req.reason}"</p>}
                        </div>
                        {req.status === 'pending' && (
                          <div className="flex gap-2">
                            <Button size="sm" className="h-7 bg-emerald-600 hover:bg-emerald-700 text-xs" onClick={() => handlePreviewRequestAction(req.id, 'approve')}><ThumbsUp className="w-3 h-3 mr-1" />Approve</Button>
                            <Button size="sm" variant="outline" className="h-7 text-xs text-red-600 border-red-200 hover:bg-red-50" onClick={() => handlePreviewRequestAction(req.id, 'reject')}><ThumbsDown className="w-3 h-3 mr-1" />Reject</Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* ─── Insights / Widgets Tab ──────────────────────────── */}
        {isSuperAdmin && (
          <TabsContent value="insights" className="space-y-4 mt-4">
            {widgetLoading ? (
              <div className="text-center py-10 text-muted-foreground">Loading insights...</div>
            ) : widgetData ? (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { label: 'Pending FW Requests', val: widgetData.framework_requests?.pending || 0, icon: <Clock className="w-4 h-4 text-amber-600" />, color: 'bg-amber-50 border-l-amber-400' },
                    { label: 'Approved FW Requests', val: widgetData.framework_requests?.approved || 0, icon: <CheckCircle className="w-4 h-4 text-emerald-600" />, color: 'bg-emerald-50 border-l-emerald-400' },
                    { label: 'Active Previews', val: widgetData.preview_modules?.active || 0, icon: <Zap className="w-4 h-4 text-orange-600" />, color: 'bg-orange-50 border-l-orange-400' },
                    { label: 'Events (24h)', val: widgetData.audit?.recent_events_24h || 0, icon: <Activity className="w-4 h-4 text-blue-600" />, color: 'bg-blue-50 border-l-blue-400' },
                  ].map((m, i) => (
                    <Card key={i} className={`border-l-4 ${m.color}`} data-testid={`widget-${i}`}>
                      <CardContent className="pt-4 pb-4"><div className="flex items-center gap-3">{m.icon}<div><p className="text-2xl font-bold">{m.val}</p><p className="text-xs text-muted-foreground">{m.label}</p></div></div></CardContent>
                    </Card>
                  ))}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Card>
                    <CardHeader><CardTitle className="text-base">Top Requested Frameworks</CardTitle></CardHeader>
                    <CardContent>
                      {(widgetData.framework_requests?.top_requested || []).length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-4">No requests yet</p>
                      ) : (
                        <div className="space-y-2">
                          {(widgetData.framework_requests?.top_requested || []).map((item, i) => (
                            <div key={i} className="flex items-center justify-between text-sm">
                              <span className="font-medium">{item.framework}</span>
                              <div className="flex items-center gap-2">
                                <div className="w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
                                  <div className="h-full bg-indigo-500 rounded-full" style={{width: `${Math.min(item.count / Math.max(...(widgetData.framework_requests?.top_requested || []).map(r => r.count)) * 100, 100)}%`}} />
                                </div>
                                <span className="text-muted-foreground w-6 text-right">{item.count}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader><CardTitle className="text-base">Active Preview Tenants</CardTitle></CardHeader>
                    <CardContent>
                      {(widgetData.preview_modules?.active_details || []).length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-4">No active previews</p>
                      ) : (
                        <div className="space-y-2">
                          {(widgetData.preview_modules?.active_details || []).map((p, i) => (
                            <div key={i} className="flex items-center justify-between text-sm border rounded-lg p-2" data-testid={`active-preview-${i}`}>
                              <div>
                                <span className="font-medium">{p.owner_email || p.name}</span>
                                <div className="flex gap-1 mt-0.5">{(p.enabled_modules || []).map(m => <span key={m} className="text-xs bg-orange-100 text-orange-700 px-1 rounded">{m}</span>)}</div>
                              </div>
                              <div className="text-right">
                                <span className="text-xs text-muted-foreground">Expires</span>
                                <p className="text-xs font-medium">{p.preview_expires_at ? new Date(p.preview_expires_at).toLocaleDateString() : '—'}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </>
            ) : (
              <Card><CardContent className="py-12 text-center text-muted-foreground">Click to load insights data</CardContent></Card>
            )}
          </TabsContent>
        )}

        {/* ─── Audit Logs Tab ──────────────────────────────── */}
        {isSuperAdmin && (
          <TabsContent value="audit" className="space-y-4 mt-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2"><Activity className="w-5 h-5 text-slate-600" />RBAC Audit Logs</CardTitle>
                  <div className="flex gap-2">
                    <div className="relative">
                      <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                      <Input placeholder="Filter by action..." value={auditSearch} onChange={(e) => { setAuditSearch(e.target.value); }} onKeyDown={(e) => e.key === 'Enter' && loadAuditLogs(auditSearch)} className="pl-8 h-8 w-48 text-sm" data-testid="audit-search" />
                    </div>
                    <Button variant="outline" size="sm" onClick={() => loadAuditLogs(auditSearch)}><RefreshCw className="w-3.5 h-3.5" /></Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {auditLoading ? (
                  <div className="text-center py-10 text-muted-foreground text-sm">Loading audit logs...</div>
                ) : auditLogs.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">No audit logs found</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="border-b bg-muted/50">
                        <th className="text-left px-4 py-2 font-medium text-muted-foreground">Timestamp</th>
                        <th className="text-left px-4 py-2 font-medium text-muted-foreground">Action</th>
                        <th className="text-left px-4 py-2 font-medium text-muted-foreground">User</th>
                        <th className="text-left px-4 py-2 font-medium text-muted-foreground">Resource</th>
                        <th className="text-left px-4 py-2 font-medium text-muted-foreground">Details</th>
                      </tr></thead>
                      <tbody className="divide-y">
                        {auditLogs.map((log, i) => (
                          <tr key={i} className="hover:bg-muted/30 transition" data-testid={`audit-row-${i}`}>
                            <td className="px-4 py-2.5 text-xs text-muted-foreground whitespace-nowrap">{log.timestamp ? new Date(log.timestamp).toLocaleString() : '—'}</td>
                            <td className="px-4 py-2.5"><Badge variant="outline" className="text-xs font-mono">{log.action}</Badge></td>
                            <td className="px-4 py-2.5 text-muted-foreground">{log.user_email || '—'}</td>
                            <td className="px-4 py-2.5"><span className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">{log.resource_type || '—'}</span></td>
                            <td className="px-4 py-2.5 text-xs text-muted-foreground max-w-[200px] truncate">{log.details ? JSON.stringify(log.details).substring(0, 80) : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* ─── Feedback Tab ───────────────────────────────────────────── */}
        {isSuperAdmin && (
          <TabsContent value="feedback" className="space-y-4" data-testid="feedback-tab-content">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">User Feedback</CardTitle>
                  <div className="flex gap-1">
                    {['all', 'up', 'down'].map(f => (
                      <Button key={f} variant={feedbackFilter === f ? 'default' : 'outline'} size="sm" className="h-7 text-xs" onClick={() => { setFeedbackFilter(f); setTimeout(loadFeedback, 0); }} data-testid={`feedback-filter-${f}`}>
                        {f === 'all' ? 'All' : f === 'up' ? <><ThumbsUp className="w-3 h-3 mr-1" />Positive</> : <><ThumbsDown className="w-3 h-3 mr-1" />Negative</>}
                      </Button>
                    ))}
                    <Button variant="outline" size="sm" className="h-7 text-xs ml-2" onClick={loadFeedback}><RefreshCw className="w-3 h-3" /></Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {feedbackLoading ? (
                  <div className="text-center py-8 text-muted-foreground text-sm">Loading feedback...</div>
                ) : feedbackItems.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground text-sm">No feedback yet</div>
                ) : (
                  <div className="space-y-3">
                    {feedbackItems.map((fb, i) => (
                      <div key={fb.id || i} className={`p-3 rounded-lg border ${fb.rating === 'up' ? 'border-emerald-200 bg-emerald-50/50' : 'border-red-200 bg-red-50/50'}`} data-testid={`feedback-item-${i}`}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              {fb.rating === 'up' ? <ThumbsUp className="w-4 h-4 text-emerald-600 flex-shrink-0" /> : <ThumbsDown className="w-4 h-4 text-red-600 flex-shrink-0" />}
                              <span className="font-medium text-sm truncate">{fb.title}</span>
                            </div>
                            {fb.description && <p className="text-xs text-muted-foreground mb-1.5 line-clamp-2">{fb.description}</p>}
                            <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
                              <span>{fb.user_email || fb.user_name || 'Anonymous'}</span>
                              <span>{fb.page_url}</span>
                              <span>{fb.created_at ? new Date(fb.created_at).toLocaleString() : ''}</span>
                            </div>
                          </div>
                          {fb.screenshot && (
                            <img
                              src={fb.screenshot}
                              alt="screenshot"
                              className="w-20 h-14 object-cover rounded border cursor-pointer hover:opacity-80 transition-opacity flex-shrink-0"
                              onClick={() => setFeedbackPreview(fb)}
                              data-testid={`feedback-screenshot-${i}`}
                            />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Feedback Screenshot Preview Dialog */}
      {feedbackPreview && (
        <Dialog open={!!feedbackPreview} onOpenChange={() => setFeedbackPreview(null)}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {feedbackPreview.rating === 'up' ? <ThumbsUp className="w-4 h-4 text-emerald-600" /> : <ThumbsDown className="w-4 h-4 text-red-600" />}
                {feedbackPreview.title}
              </DialogTitle>
              <DialogDescription>
                {feedbackPreview.user_email} &middot; {feedbackPreview.page_url} &middot; {feedbackPreview.created_at ? new Date(feedbackPreview.created_at).toLocaleString() : ''}
              </DialogDescription>
            </DialogHeader>
            {feedbackPreview.description && <p className="text-sm text-muted-foreground">{feedbackPreview.description}</p>}
            {feedbackPreview.screenshot && (
              <img src={feedbackPreview.screenshot} alt="feedback screenshot" className="w-full rounded-lg border" data-testid="feedback-preview-image" />
            )}
          </DialogContent>
        </Dialog>
      )}

      {/* Create Tenant Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Create New Tenant</DialogTitle><DialogDescription>Create a new organization with license and module configuration.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label htmlFor="name">Organization Name *</Label><Input id="name" placeholder="Acme Corporation" value={newTenant.name} onChange={(e) => setNewTenant(prev => ({ ...prev, name: e.target.value }))} /></div>
            <div className="space-y-2"><Label htmlFor="slug">URL Slug *</Label><Input id="slug" placeholder="acme-corp" value={newTenant.slug} onChange={(e) => setNewTenant(prev => ({ ...prev, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') }))} /><p className="text-xs text-gray-500">Only lowercase letters, numbers, and hyphens</p></div>
            <div className="space-y-2"><Label htmlFor="owner_email">Owner Email *</Label><Input id="owner_email" type="email" placeholder="admin@acme.com" value={newTenant.owner_email} onChange={(e) => setNewTenant(prev => ({ ...prev, owner_email: e.target.value }))} /><p className="text-xs text-gray-500">This user must already be registered</p></div>
            <div className="space-y-2"><Label htmlFor="domains">Allowed Domains</Label><Input id="domains" placeholder="acme.com, acme.co.uk" value={newTenant.domains} onChange={(e) => setNewTenant(prev => ({ ...prev, domains: e.target.value }))} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="license">License Tier</Label>
                <Select value={newTenant.license_tier} onValueChange={(v) => setNewTenant(prev => ({ ...prev, license_tier: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{licenseTiers.map(tier => { const k = tier.tier || tier.id || ''; return <SelectItem key={k} value={k}>{k.charAt(0).toUpperCase() + k.slice(1)}</SelectItem>; })}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label htmlFor="max_users">Max Users</Label><Input id="max_users" type="number" min={1} value={newTenant.max_users} onChange={(e) => setNewTenant(prev => ({ ...prev, max_users: parseInt(e.target.value) || 5 }))} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateTenant} disabled={creating} className="bg-indigo-600 hover:bg-indigo-700">{creating ? 'Creating...' : 'Create Tenant'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Module Assignment Dialog */}
      <Dialog open={showModuleDialog} onOpenChange={setShowModuleDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Layers className="w-5 h-5 text-indigo-600" />Manage Modules</DialogTitle>
            <DialogDescription>{selectedTenant && <span>Configure platform modules for <strong>{selectedTenant.name}</strong></span>}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-3">
              {(availableModules.length > 0 ? availableModules : [
                { id: 'grc', name: 'GRC Nexus', description: 'Governance, Risk & Compliance' },
                { id: 'osint', name: 'OSINT', description: 'Open Source Intelligence' },
                { id: 'litesabre', name: 'LiteSabre', description: 'Vulnerability Scanner' },
                { id: 'amidala_cspm', name: 'Amidala CSPM', description: 'Cloud Security Posture Management' },
                { id: 'active_directory', name: 'Active Directory', description: 'AD Security Analysis' }
              ]).map((module) => (
                <div key={module.id} className={`flex items-center justify-between p-4 border rounded-lg transition-colors cursor-pointer ${selectedModules.includes(module.id) ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 hover:border-gray-300'}`} onClick={() => handleModuleToggle(module.id)} data-testid={`module-toggle-${module.id}`}>
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{getModuleIcon(module.id)}</span>
                    <div><h4 className="font-medium">{module.name}</h4><p className="text-sm text-muted-foreground">{module.description}</p></div>
                  </div>
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${selectedModules.includes(module.id) ? 'border-indigo-600 bg-indigo-600' : 'border-gray-300'}`}>
                    {selectedModules.includes(module.id) && <CheckCircle className="w-4 h-4 text-white" />}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
              <p className="text-sm text-amber-800">Disabling modules will hide them from the tenant's sidebar but won't delete any data.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModuleDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveModules} disabled={savingModules} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-modules-btn">
              {savingModules ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Saving...</> : <><Save className="w-4 h-4 mr-2" />Save Changes</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Visible Frameworks Dialog */}
      <Dialog open={showFrameworkDialog} onOpenChange={setShowFrameworkDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileText className="w-5 h-5 text-indigo-600" />Manage Visible Frameworks</DialogTitle>
            <DialogDescription>{frameworkTenant && <span>Select which compliance frameworks are visible to <strong>{frameworkTenant.name}</strong>. Tenants can only request frameworks they don't see.</span>}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Search frameworks..." value={frameworkSearch} onChange={(e) => setFrameworkSearch(e.target.value)} className="pl-10" data-testid="framework-search-input" />
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span>{visibleFrameworks.length} selected</span>
              <Button variant="link" size="sm" className="text-xs h-auto p-0" onClick={() => setVisibleFrameworks(allFrameworks.map(f => f.code.toUpperCase()))}>Select All</Button>
              <Button variant="link" size="sm" className="text-xs h-auto p-0" onClick={() => setVisibleFrameworks([])}>Clear All</Button>
            </div>
            <div className="max-h-[40vh] overflow-y-auto space-y-1 border rounded-lg p-2">
              {filteredAllFrameworks.map((fw) => {
                const checked = visibleFrameworks.includes(fw.code.toUpperCase());
                return (
                  <div key={fw.code} className={`flex items-center justify-between px-3 py-2 rounded cursor-pointer transition-colors ${checked ? 'bg-indigo-50 border border-indigo-200' : 'hover:bg-gray-50 border border-transparent'}`} onClick={() => handleFrameworkToggle(fw.code)} data-testid={`fw-toggle-${fw.code}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm">{fw.code}</span>
                        <span className="text-xs text-muted-foreground truncate">{fw.name}</span>
                      </div>
                    </div>
                    <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${checked ? 'border-indigo-600 bg-indigo-600' : 'border-gray-300'}`}>
                      {checked && <CheckCircle className="w-3 h-3 text-white" />}
                    </div>
                  </div>
                );
              })}
              {filteredAllFrameworks.length === 0 && <p className="text-center py-4 text-muted-foreground text-sm">No frameworks match your search</p>}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFrameworkDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveFrameworks} disabled={savingFrameworks} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-frameworks-btn">
              {savingFrameworks ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Saving...</> : <><Save className="w-4 h-4 mr-2" />Save ({visibleFrameworks.length} frameworks)</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Modules Dialog */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Zap className="w-5 h-5 text-orange-500" />Add Preview Modules</DialogTitle>
            <DialogDescription>{previewUser && <span>Activate a 7-day preview for <strong>{previewUser.email}</strong>. A tenant with slug <code className="bg-gray-100 px-1 rounded">preview-{previewUser.email?.split('@')[0]?.toLowerCase().replace(/[._]/g, '-')}</code> will be created.</span>}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-4">
            {[
              { id: 'grc', name: 'GRC Nexus', desc: 'Governance, Risk & Compliance' },
              { id: 'osint', name: 'OSINT', desc: 'Open Source Intelligence' },
              { id: 'litesabre', name: 'LiteSabre', desc: 'Vulnerability Scanner' },
              { id: 'amidala', name: 'Amidala CSPM', desc: 'Cloud Security' },
              { id: 'sbom', name: 'SBOM', desc: 'Software Bill of Materials' },
            ].map((mod) => (
              <div key={mod.id} className={`flex items-center justify-between p-3 border rounded-lg cursor-pointer transition-colors ${previewModules.includes(mod.id) ? 'border-orange-400 bg-orange-50' : 'border-gray-200 hover:border-gray-300'}`} onClick={() => handlePreviewToggle(mod.id)} data-testid={`preview-module-${mod.id}`}>
                <div className="flex items-center gap-3">
                  <span className="text-xl">{getModuleIcon(mod.id)}</span>
                  <div><h4 className="font-medium text-sm">{mod.name}</h4><p className="text-xs text-muted-foreground">{mod.desc}</p></div>
                </div>
                <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${previewModules.includes(mod.id) ? 'border-orange-500 bg-orange-500' : 'border-gray-300'}`}>
                  {previewModules.includes(mod.id) && <CheckCircle className="w-3 h-3 text-white" />}
                </div>
              </div>
            ))}
            <div className="flex items-center gap-2 p-3 bg-orange-50 border border-orange-200 rounded-lg">
              <Clock className="w-4 h-4 text-orange-600 flex-shrink-0" />
              <p className="text-sm text-orange-800">Preview access expires after 7 days.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreviewDialog(false)}>Cancel</Button>
            <Button onClick={handleSavePreview} disabled={savingPreview} className="bg-orange-500 hover:bg-orange-600" data-testid="save-preview-btn">
              {savingPreview ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Activating...</> : <><Zap className="w-4 h-4 mr-2" />Activate Preview</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
